import {
  isAddressNumberToken,
  isPostcodeToken,
  normaliseAddressQuery,
  normaliseMacrons,
  parseAddressQuery,
  STREETKIT_LINZ_LICENCE,
  detailBucketForId as sharedDetailBucketForId,
  detailBucketPrefixLengthFromStrategy,
  tokeniseAddressQuery,
  type AddressDetail,
  type CompactShardAddress,
  type StreetKitAccessMode,
  type StreetKitAddress,
  type StreetKitError,
  type StreetKitErrorCode,
  type StreetKitManifestFile,
  type StreetKitSearchResult,
  type StreetKitSelectedAddress
} from "@streetkit/shared";

import {
  rankCandidates,
  toPublicSearchResult,
  type RankedStreetKitCandidate
} from "./rank.js";

export const DEFAULT_STREETKIT_INDEX_BASE_URL = "/index/v1";
export const DEFAULT_STREETKIT_LIMIT = 8;
export const DEFAULT_STREETKIT_MIN_QUERY_LENGTH = 3;
export const DEFAULT_STREETKIT_DEBOUNCE_MS = 200;
export const DEFAULT_STREETKIT_SHARD_CACHE_SIZE = 64;
export const DEFAULT_STREETKIT_QUERY_CACHE_SIZE = 32;
export const DEFAULT_STREETKIT_HARD_SHARD_GZIP_BYTES = 750_000;
export const MAX_STREETKIT_RENDERED_SUGGESTIONS = 12;
export const PREFIX_SHARD_LENGTH = 2;
export const MAX_QUERY_SHARD_KEYS = 4;
export const ADAPTIVE_PREFIX_SHARD_STRATEGY_NAME = "adaptive-prefix-v1";
export const STREETKIT_SITE_ID_PATTERN = /^sk_(?:live|test)_[a-z0-9]{6,32}$/u;
const STREETKIT_SITE_QUERY_PARAM_PATTERN = /^[A-Za-z0-9._~-]{1,64}$/u;
const MAX_REFINEMENT_CACHE_RECORDS = 10_000;
const MAX_BROAD_CHILD_FALLBACK_KEYS = 32;
const MAX_BROAD_CHILD_FALLBACK_RECORDS = 25_000;
const MAX_BROAD_CHILD_FALLBACK_GZIP_BYTES = 1_250_000;

export type StreetKitFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export type StreetKitClientConfig = {
  indexBaseUrl?: string;
  siteId?: string;
  publicMode?: boolean;
  mode?: StreetKitAccessMode;
  fetcher?: StreetKitFetch;
  limit?: number;
  minQueryLength?: number;
  shardCacheSize?: number;
  queryCacheSize?: number;
};

export type StreetKitSearchOptions = {
  limit?: number;
  minQueryLength?: number;
  signal?: AbortSignal;
};

export type StreetKitSearchPlanStatus =
  | "search"
  | "empty"
  | "too_short"
  | "too_broad"
  | "no_route";

export type StreetKitSearchPlan = {
  status: StreetKitSearchPlanStatus;
  message: string;
  minQueryLength: number;
  shardKeys: string[];
  broadTerms: string[];
  routeTerms: string[];
};

export type StreetKitSearchCandidatesResponse = {
  candidates: RankedStreetKitCandidate[];
  plan: StreetKitSearchPlan;
};

export type StreetKitIndexInfo = {
  meta: StreetKitMetaJson;
  manifest: StreetKitManifestJson | null;
  routing: StreetKitShardRoutingJson | null;
  access: StreetKitResolvedAccess;
  assetBaseUrl: string;
  datasetVersion: string;
  minQueryLength: number;
  prefixLength: number;
  shardStrategy: string;
  warnings: string[];
  largeShardKeys: string[];
};

export type StreetKitMetaJson = {
  schema_version?: string;
  schemaVersion?: string;
  current_version?: string;
  currentVersion?: string;
  dataset_version?: string;
  datasetVersion?: string;
  version?: string;
  base_url?: string;
  baseUrl?: string;
  manifest_url?: string;
  manifestUrl?: string;
  min_query_length?: number;
  minQueryLength?: number;
  shards?: {
    strategy?: string;
    prefix_length?: number;
    prefixLength?: number;
    min_query_length?: number;
    minQueryLength?: number;
    template?: string;
    routing_url?: string | null;
    routingUrl?: string | null;
    split_map_url?: string | null;
    splitMapUrl?: string | null;
    postcode_template?: string | null;
    postcodeTemplate?: string | null;
  };
  detail?: {
    template?: string;
    bucket_strategy?: string;
    bucketStrategy?: string;
    bucket_prefix_length?: number;
    bucketPrefixLength?: number;
  };
  access?: StreetKitAccessMetadata;
  source?: Record<string, unknown>;
};

export type StreetKitAccessMetadata = {
  mode?: string;
  siteId?: string;
  site_id?: string;
  siteRequired?: boolean;
  site_required?: boolean;
  siteIdRequired?: boolean;
  site_id_required?: boolean;
  queryParam?: string | null;
  query_param?: string | null;
  status?: string;
};

export type StreetKitManifestJson = {
  schema_version?: string;
  schemaVersion?: string;
  dataset_version?: string;
  datasetVersion?: string;
  version?: string;
  min_query_length?: number;
  minQueryLength?: number;
  shards?: {
    strategy?: string;
    prefix_length?: number;
    prefixLength?: number;
    min_query_length?: number;
    minQueryLength?: number;
    target_gzip_bytes?: number;
    targetGzipBytes?: number;
    hard_gzip_bytes?: number;
    hardGzipBytes?: number;
    target_record_count?: number;
    targetRecordCount?: number;
    hard_record_count?: number;
    hardRecordCount?: number;
    routing_url?: string | null;
    routingUrl?: string | null;
    files?: StreetKitManifestShardFile[];
  };
  largest_shard_size?: StreetKitManifestShardSize;
  largestShardSize?: StreetKitManifestShardSize;
  warnings?: string[];
};

export type StreetKitManifestShardFile = StreetKitManifestFile & {
  key?: string;
  recordCount?: number | null;
  gzipBytes?: number | null;
};

export type StreetKitShardRoute = {
  prefix?: string;
  path?: string;
  too_broad?: boolean;
  tooBroad?: boolean;
  min_query_length?: number;
  minQueryLength?: number;
  children?: string[];
  record_count?: number;
  recordCount?: number;
  bytes?: number | null;
  gzip_bytes?: number | null;
  gzipBytes?: number | null;
};

export type StreetKitShardRoutingJson = {
  schema_version?: string;
  schemaVersion?: string;
  strategy?: string;
  dataset_version?: string;
  datasetVersion?: string;
  version?: string;
  defaults?: {
    base_prefix_length?: number;
    basePrefixLength?: number;
    max_prefix_length?: number;
    maxPrefixLength?: number;
    min_query_length?: number;
    minQueryLength?: number;
    target_record_count?: number;
    targetRecordCount?: number;
    hard_record_count?: number;
    hardRecordCount?: number;
    target_gzip_bytes?: number;
    targetGzipBytes?: number;
    hard_gzip_bytes?: number;
    hardGzipBytes?: number;
  };
  routes?: Record<string, StreetKitShardRoute>;
};

export type StreetKitManifestShardSize = {
  path?: string | null;
  bytes?: number | null;
  gzip_bytes?: number | null;
  gzipBytes?: number | null;
  record_count?: number | null;
  recordCount?: number | null;
};

type StreetKitDetailBucketJson = {
  records?: Record<string, AddressDetail>;
};

type StreetKitDetailPayload = AddressDetail | StreetKitDetailBucketJson;

export class StreetKitHttpError extends Error {
  readonly status: number;
  readonly url: string;

  constructor(url: string, status: number) {
    super(`StreetKit request failed with ${status}: ${url}`);
    this.name = "StreetKitHttpError";
    this.url = url;
    this.status = status;
  }
}

export class StreetKitConfigError extends Error {
  readonly code: StreetKitErrorCode = "invalid_config";
  readonly streetkitError: StreetKitError;

  constructor(message: string, context: Omit<StreetKitError, "code" | "message"> = {}) {
    super(message);
    this.name = "StreetKitConfigError";
    this.streetkitError = {
      code: "invalid_config",
      message,
      ...context
    };
  }
}

export type StreetKitResolvedAccess = {
  mode: StreetKitAccessMode;
  siteId: string | null;
  appendSiteParam: boolean;
  siteQueryParam: string;
};

export class StreetKitIndexClient {
  private readonly baseUrl: string;
  private readonly fetcher: StreetKitFetch;
  private readonly accessConfig: StreetKitResolvedAccess;
  private readonly configuredLimit: number;
  private readonly configuredMinQueryLength: number | undefined;
  private readonly shardCacheSize: number;
  private readonly queryCacheSize: number;
  private metaPromise: Promise<StreetKitMetaJson> | null = null;
  private manifestPromise: Promise<StreetKitManifestJson | null> | null = null;
  private routingPromise: Promise<StreetKitShardRoutingJson | null> | null = null;
  private resolvedAccess: StreetKitResolvedAccess | null = null;
  private readonly shardCache = new Map<string, Promise<CompactShardAddress[]>>();
  private readonly queryResultCache = new Map<string, RankedStreetKitCandidate[]>();
  private readonly queryRefinementCache = new Map<string, QueryRefinementCacheEntry>();
  private readonly detailCache = new Map<string, Promise<StreetKitDetailPayload>>();

  constructor(config: StreetKitClientConfig = {}) {
    const defaultFetcher = globalThis.fetch;

    if (config.fetcher === undefined && typeof defaultFetcher !== "function") {
      throw new Error("StreetKit requires fetch support or a custom fetcher.");
    }

    this.baseUrl = normaliseIndexBaseUrl(config.indexBaseUrl);
    this.accessConfig = resolveAccessConfig({
      baseUrl: this.baseUrl,
      mode: config.mode,
      publicMode: config.publicMode,
      siteId: config.siteId
    });
    this.fetcher = config.fetcher ?? defaultFetcher.bind(globalThis);
    this.configuredLimit = config.limit ?? DEFAULT_STREETKIT_LIMIT;
    this.configuredMinQueryLength = config.minQueryLength;
    this.shardCacheSize = normaliseCacheSize(
      config.shardCacheSize,
      DEFAULT_STREETKIT_SHARD_CACHE_SIZE
    );
    this.queryCacheSize = normaliseCacheSize(
      config.queryCacheSize,
      DEFAULT_STREETKIT_QUERY_CACHE_SIZE
    );
  }

  async search(
    rawQuery: string,
    options: StreetKitSearchOptions = {}
  ): Promise<StreetKitSearchResult[]> {
    const response = await this.searchCandidatesWithPlan(rawQuery, options);
    return response.candidates.map(toPublicSearchResult);
  }

  async searchCandidates(
    rawQuery: string,
    options: StreetKitSearchOptions = {}
  ): Promise<RankedStreetKitCandidate[]> {
    const response = await this.searchCandidatesWithPlan(rawQuery, options);
    return response.candidates;
  }

  async searchCandidatesWithPlan(
    rawQuery: string,
    options: StreetKitSearchOptions = {}
  ): Promise<StreetKitSearchCandidatesResponse> {
    throwIfAborted(options.signal);

    const queryLength = normaliseQueryForLength(rawQuery);
    const configuredMinQueryLength = options.minQueryLength ?? this.configuredMinQueryLength;

    if (queryLength.length === 0) {
      return {
        candidates: [],
        plan: emptySearchPlan("empty", DEFAULT_STREETKIT_MIN_QUERY_LENGTH)
      };
    }

    if (
      configuredMinQueryLength !== undefined &&
      queryLength.length < configuredMinQueryLength
    ) {
      return {
        candidates: [],
        plan: emptySearchPlan("too_short", configuredMinQueryLength)
      };
    }

    const info = await this.getIndexInfo();
    throwIfAborted(options.signal);

    const minQueryLength = options.minQueryLength ?? this.configuredMinQueryLength ?? info.minQueryLength;

    if (queryLength.length < minQueryLength) {
      return {
        candidates: [],
        plan: emptySearchPlan("too_short", minQueryLength)
      };
    }

    const selection = selectShardKeysForSearch(rawQuery, info, minQueryLength);
    const limit = options.limit ?? this.configuredLimit;

    if (selection.status !== "search") {
      const broadFallback = await this.searchBroadChildFallback(rawQuery, info, selection, {
        limit,
        minQueryLength,
        signal: options.signal
      });

      if (broadFallback !== null) {
        return broadFallback;
      }

      const refined = this.getCachedRefinementCandidates(rawQuery, info, {
        limit,
        minQueryLength
      });

      if (refined !== null) {
        return refined;
      }

      return {
        candidates: [],
        plan: selectionToSearchPlan(selection, minQueryLength)
      };
    }

    const shardKeys = selection.keys;
    const queryCacheKey = searchCacheKey({
      datasetVersion: info.datasetVersion,
      limit,
      minQueryLength,
      prefixLength: info.prefixLength,
      query: normaliseQueryCacheKey(rawQuery),
      shardKeys
    });
    const cached = this.getCachedQueryResult(queryCacheKey);

    if (cached !== undefined) {
      return {
        candidates: cached,
        plan: selectionToSearchPlan(selection, minQueryLength)
      };
    }

    const records = await this.fetchShardRecords(shardKeys, info, options.signal);
    throwIfAborted(options.signal);
    const ranked = rankCandidates(records, {
      rawQuery,
      datasetVersion: info.datasetVersion,
      limit
    });

    if (ranked.length > 0) {
      this.setCachedRefinementRecords(rawQuery, info, {
        minQueryLength,
        records,
        selection
      });
    }

    if (ranked.length === 0) {
      const broadFallback = await this.searchBroadChildFallback(rawQuery, info, selection, {
        limit,
        minQueryLength,
        signal: options.signal
      });

      if (broadFallback !== null) {
        this.setCachedQueryResult(queryCacheKey, broadFallback.candidates);
        return broadFallback;
      }

      const refined = this.getCachedRefinementCandidates(rawQuery, info, {
        limit,
        minQueryLength
      });

      if (refined !== null) {
        this.setCachedQueryResult(queryCacheKey, refined.candidates);
        return refined;
      }
    }

    this.setCachedQueryResult(queryCacheKey, ranked);
    return {
      candidates: ranked,
      plan: selectionToSearchPlan(selection, minQueryLength)
    };
  }

  async getIndexInfo(): Promise<StreetKitIndexInfo> {
    const meta = await this.fetchMeta();
    const assetBaseUrl = resolveAssetBaseUrl(meta, this.baseUrl);
    const access = resolveMetadataAccess(this.accessConfig, meta, this.baseUrl);
    this.resolvedAccess = access;
    const manifest = await this.fetchManifest(assetBaseUrl);
    const routing = await this.fetchRouting(meta, manifest, assetBaseUrl);
    const datasetVersion = stringFrom(
      meta.dataset_version,
      meta.datasetVersion,
      meta.current_version,
      meta.currentVersion,
      meta.version,
      manifest?.dataset_version,
      manifest?.datasetVersion,
      manifest?.version
    );
    const minQueryLength =
      numberFrom(
        manifest?.min_query_length,
        manifest?.minQueryLength,
        manifest?.shards?.min_query_length,
        manifest?.shards?.minQueryLength,
        meta.min_query_length,
        meta.minQueryLength,
        meta.shards?.min_query_length,
        meta.shards?.minQueryLength
      ) ?? DEFAULT_STREETKIT_MIN_QUERY_LENGTH;
    const prefixLength =
      numberFrom(
        manifest?.shards?.prefix_length,
        manifest?.shards?.prefixLength,
        meta.shards?.prefix_length,
        meta.shards?.prefixLength
      ) ?? PREFIX_SHARD_LENGTH;
    const shardStrategy =
      stringFrom(manifest?.shards?.strategy, meta.shards?.strategy) ??
      (routing === null ? "prefix-v1" : ADAPTIVE_PREFIX_SHARD_STRATEGY_NAME);

    return {
      meta,
      manifest,
      routing,
      access,
      assetBaseUrl,
      datasetVersion: datasetVersion ?? "unknown",
      minQueryLength,
      prefixLength,
      shardStrategy,
      warnings: manifestWarnings(manifest),
      largeShardKeys: largeShardKeysFromManifest(manifest)
    };
  }

  async getDetailForRecord(
    record: CompactShardAddress,
    score?: number
  ): Promise<StreetKitAddress> {
    const detailPath = record.detailPath;

    if (detailPath === null) {
      throw new Error(`StreetKit detail path missing for ${record.id}`);
    }

    const detail = await this.fetchDetailRecord(detailPath, record.id);

    if (detail === null) {
      throw new Error(`StreetKit detail record missing for ${record.id}`);
    }

    return toSelectedAddress(detail, record, score);
  }

  async getDetailById(id: string, score?: number): Promise<StreetKitAddress> {
    const info = await this.getIndexInfo();
    const path = detailPathForId(id, {
      template: info.meta.detail?.template,
      bucketStrategy: info.meta.detail?.bucketStrategy ?? info.meta.detail?.bucket_strategy,
      bucketPrefixLength:
        info.meta.detail?.bucketPrefixLength ?? info.meta.detail?.bucket_prefix_length
    });
    const detail = await this.fetchDetailRecord(path, id);

    if (detail === null) {
      throw new Error(`StreetKit detail record missing for ${id}`);
    }

    return toSelectedAddress(detail, null, score);
  }

  clearCache(): void {
    this.metaPromise = null;
    this.manifestPromise = null;
    this.routingPromise = null;
    this.resolvedAccess = null;
    this.shardCache.clear();
    this.queryResultCache.clear();
    this.detailCache.clear();
  }

  shardKeysForQuery(rawQuery: string, options: StreetKitSearchOptions = {}): string[] {
    const minQueryLength =
      options.minQueryLength ?? this.configuredMinQueryLength ?? DEFAULT_STREETKIT_MIN_QUERY_LENGTH;
    return shardKeysForQuery(rawQuery, { minQueryLength });
  }

  private fetchMeta(): Promise<StreetKitMetaJson> {
    this.metaPromise ??= this.fetchJson<StreetKitMetaJson>(
      resolveIndexUrl(this.baseUrl, "meta.json")
    ).catch((error: unknown) => {
      this.metaPromise = null;
      throw error;
    });
    return this.metaPromise;
  }

  private async fetchManifest(assetBaseUrl: string): Promise<StreetKitManifestJson | null> {
    if (this.manifestPromise !== null) {
      return this.manifestPromise;
    }

    this.manifestPromise = this.fetchManifestInner(assetBaseUrl).catch((error: unknown) => {
      this.manifestPromise = null;
      throw error;
    });
    return this.manifestPromise;
  }

  private async fetchManifestInner(assetBaseUrl: string): Promise<StreetKitManifestJson | null> {
    const meta = await this.fetchMeta();
    const manifestPath = meta.manifest_url ?? meta.manifestUrl;

    if (manifestPath === undefined || manifestPath.trim().length === 0) {
      return null;
    }

    try {
      return await this.fetchJson<StreetKitManifestJson>(resolveIndexUrl(assetBaseUrl, manifestPath));
    } catch (error) {
      if (error instanceof StreetKitHttpError && error.status === 404) {
        return null;
      }

      throw error;
    }
  }

  private async fetchRouting(
    meta: StreetKitMetaJson,
    manifest: StreetKitManifestJson | null,
    assetBaseUrl: string
  ): Promise<StreetKitShardRoutingJson | null> {
    if (this.routingPromise !== null) {
      return this.routingPromise;
    }

    this.routingPromise = this.fetchRoutingInner(meta, manifest, assetBaseUrl).catch((error: unknown) => {
      this.routingPromise = null;
      throw error;
    });
    return this.routingPromise;
  }

  private async fetchRoutingInner(
    meta: StreetKitMetaJson,
    manifest: StreetKitManifestJson | null,
    assetBaseUrl: string
  ): Promise<StreetKitShardRoutingJson | null> {
    const routingPath = stringFrom(
      manifest?.shards?.routing_url,
      manifest?.shards?.routingUrl,
      meta.shards?.routing_url,
      meta.shards?.routingUrl,
      meta.shards?.split_map_url,
      meta.shards?.splitMapUrl
    );

    if (routingPath === null) {
      return null;
    }

    try {
      return await this.fetchJson<StreetKitShardRoutingJson>(
        resolveIndexUrl(assetBaseUrl, routingPath)
      );
    } catch (error) {
      if (error instanceof StreetKitHttpError && error.status === 404) {
        return null;
      }

      throw error;
    }
  }

  private async fetchShardRecords(
    keys: string[],
    info: StreetKitIndexInfo,
    signal: AbortSignal | undefined
  ): Promise<CompactShardAddress[]> {
    const shards = await Promise.all(keys.map((key) => this.fetchShard(key, info, signal)));
    const deduped = new Map<string, CompactShardAddress>();

    for (const record of shards.flat()) {
      deduped.set(record.id, record);
    }

    return [...deduped.values()];
  }

  private fetchShard(
    key: string,
    info: StreetKitIndexInfo,
    signal: AbortSignal | undefined
  ): Promise<CompactShardAddress[]> {
    const cached = readCachedValue(this.shardCache, key);

    if (cached !== undefined) {
      return cached;
    }

    const request = this.fetchShardInner(key, info, signal).catch((error: unknown) => {
      this.shardCache.delete(key);
      throw error;
    });
    setBoundedCacheValue(this.shardCache, key, request, this.shardCacheSize);
    return request;
  }

  private async fetchShardInner(
    key: string,
    info: StreetKitIndexInfo,
    signal: AbortSignal | undefined
  ): Promise<CompactShardAddress[]> {
    const shardUrl = this.shardUrlForKey(key, info);
    const url = shardUrl === null ? null : withSiteQuery(shardUrl, info.access);

    if (url === null) {
      return [];
    }

    try {
      const payload = await this.fetchJson<{ records?: CompactShardAddress[] }>(url, signal);
      return payload.records ?? [];
    } catch (error) {
      if (error instanceof StreetKitHttpError && error.status === 404) {
        return [];
      }

      throw error;
    }
  }

  private shardUrlForKey(key: string, info: StreetKitIndexInfo): string | null {
    const route = info.routing?.routes?.[key];

    if (route?.path !== undefined && route.path.trim().length > 0) {
      return resolveIndexUrl(info.assetBaseUrl, route.path);
    }

    const file = info.manifest?.shards?.files?.find((candidate) => candidate.key === key);

    if (file?.path !== undefined) {
      return resolveIndexUrl(info.assetBaseUrl, file.path);
    }

    const template = info.meta.shards?.template;

    if (template !== undefined && template.trim().length > 0) {
      return resolveIndexUrl(
        info.assetBaseUrl,
        template.replaceAll("{first}", key.charAt(0)).replaceAll("{prefix}", key)
      );
    }

    return resolveIndexUrl(info.assetBaseUrl, shardPathForKey(key));
  }

  private async fetchDetailRecord(path: string, id: string): Promise<AddressDetail | null> {
    const payload = await this.fetchDetailPayload(path);
    return detailFromPayload(payload, id);
  }

  private async fetchDetailPayload(path: string): Promise<StreetKitDetailPayload> {
    const cached = readCachedValue(this.detailCache, path);

    if (cached !== undefined) {
      return cached;
    }

    const meta = await this.fetchMeta();
    const access = await this.getResolvedAccess();
    const assetBaseUrl = resolveAssetBaseUrl(meta, this.baseUrl);
    const request = this.fetchJson<StreetKitDetailPayload>(
      withSiteQuery(resolveIndexUrl(assetBaseUrl, path), access)
    );
    this.detailCache.set(path, request);
    return request;
  }

  private async getResolvedAccess(): Promise<StreetKitResolvedAccess> {
    if (this.resolvedAccess !== null) {
      return this.resolvedAccess;
    }

    const meta = await this.fetchMeta();
    const access = resolveMetadataAccess(this.accessConfig, meta, this.baseUrl);
    this.resolvedAccess = access;
    return access;
  }

  private async searchBroadChildFallback(
    rawQuery: string,
    info: StreetKitIndexInfo,
    selection: ShardSelection,
    options: {
      limit: number;
      minQueryLength: number;
      signal?: AbortSignal | undefined;
    }
  ): Promise<StreetKitSearchCandidatesResponse | null> {
    if (
      info.shardStrategy !== ADAPTIVE_PREFIX_SHARD_STRATEGY_NAME ||
      info.routing?.routes === undefined ||
      selection.broadTerms.length === 0 ||
      !hasLeadingAddressNumberIntent(rawQuery)
    ) {
      return null;
    }

    const keys = broadChildFallbackKeys(selection.broadTerms, info.routing);

    if (keys.length === 0) {
      return null;
    }

    const records = await this.fetchShardRecords(keys, info, options.signal);
    throwIfAborted(options.signal);
    const ranked = rankCandidates(records, {
      rawQuery,
      datasetVersion: info.datasetVersion,
      limit: options.limit
    });

    if (ranked.length === 0) {
      return null;
    }

    return {
      candidates: ranked,
      plan: {
        status: "search",
        message: "Search route selected from bounded road shards.",
        minQueryLength: options.minQueryLength,
        shardKeys: keys,
        broadTerms: selection.broadTerms,
        routeTerms: uniqueTokens([...selection.routeTerms, ...keys])
      }
    };
  }

  private async fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
    const requestInit: RequestInit = {
      credentials: "omit"
    };

    if (signal !== undefined) {
      requestInit.signal = signal;
    }

    const response = await this.fetcher(url, requestInit);

    if (!response.ok) {
      throw new StreetKitHttpError(url, response.status);
    }

    return (await response.json()) as T;
  }

  private getCachedQueryResult(key: string): RankedStreetKitCandidate[] | undefined {
    return readCachedValue(this.queryResultCache, key);
  }

  private setCachedQueryResult(key: string, results: RankedStreetKitCandidate[]): void {
    setBoundedCacheValue(this.queryResultCache, key, results, this.queryCacheSize);
  }

  private setCachedRefinementRecords(
    rawQuery: string,
    info: StreetKitIndexInfo,
    options: {
      minQueryLength: number;
      records: CompactShardAddress[];
      selection: ShardSelection;
    }
  ): void {
    const query = normaliseQueryCacheKey(rawQuery);

    if (
      query.length === 0 ||
      options.records.length === 0 ||
      options.records.length > MAX_REFINEMENT_CACHE_RECORDS
    ) {
      return;
    }

    setBoundedCacheValue(
      this.queryRefinementCache,
      queryRefinementCacheKey({
        datasetVersion: info.datasetVersion,
        minQueryLength: options.minQueryLength,
        prefixLength: info.prefixLength,
        query
      }),
      {
        datasetVersion: info.datasetVersion,
        minQueryLength: options.minQueryLength,
        prefixLength: info.prefixLength,
        query,
        records: options.records,
        shardKeys: options.selection.keys,
        broadTerms: options.selection.broadTerms,
        routeTerms: options.selection.routeTerms
      },
      this.queryCacheSize
    );
  }

  private getCachedRefinementCandidates(
    rawQuery: string,
    info: StreetKitIndexInfo,
    options: {
      limit: number;
      minQueryLength: number;
    }
  ): StreetKitSearchCandidatesResponse | null {
    const query = normaliseQueryCacheKey(rawQuery);

    if (query.length === 0) {
      return null;
    }

    const entries = [...this.queryRefinementCache.values()].reverse();

    for (const entry of entries) {
      if (
        entry.datasetVersion !== info.datasetVersion ||
        entry.minQueryLength !== options.minQueryLength ||
        entry.prefixLength !== info.prefixLength ||
        !isReusableCachedQuery(entry.query, query)
      ) {
        continue;
      }

      const ranked = rankCandidates(entry.records, {
        rawQuery,
        datasetVersion: info.datasetVersion,
        limit: options.limit
      });

      if (ranked.length === 0) {
        continue;
      }

      const cacheKey = queryRefinementCacheKey({
        datasetVersion: entry.datasetVersion,
        minQueryLength: entry.minQueryLength,
        prefixLength: entry.prefixLength,
        query: entry.query
      });
      readCachedValue(this.queryRefinementCache, cacheKey);

      return {
        candidates: ranked,
        plan: refinementSearchPlan(entry, options.minQueryLength)
      };
    }

    return null;
  }
}

export type ShardKeyOptions = {
  minQueryLength?: number;
  prefixLength?: number;
  maxShardKeys?: number;
};

export function normaliseIndexBaseUrl(
  indexBaseUrl = DEFAULT_STREETKIT_INDEX_BASE_URL
): string {
  const trimmed = indexBaseUrl.trim();

  if (trimmed.length === 0) {
    throw new StreetKitConfigError("StreetKit indexBaseUrl must not be empty.", {
      indexBaseUrl: trimmed
    });
  }

  return trimmed === "/" ? "/" : trimmed.replace(/\/+$/u, "");
}

export function resolveIndexUrl(baseUrl: string, path: string): string {
  if (/^https?:\/\//iu.test(path)) {
    return path;
  }

  if (/^https?:\/\//iu.test(baseUrl)) {
    return new URL(path, baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`).toString();
  }

  if (path.startsWith("/")) {
    return path;
  }

  if (baseUrl === "/") {
    return `/${path.replace(/^\/+/u, "")}`;
  }

  return baseUrl.length === 0 ? path : `${baseUrl}/${path.replace(/^\/+/u, "")}`;
}

function resolveAssetBaseUrl(meta: StreetKitMetaJson, configuredBaseUrl: string): string {
  const metadataBaseUrl = stringFrom(meta.base_url, meta.baseUrl);
  const metadataManifestUrl = stringFrom(meta.manifest_url, meta.manifestUrl);
  const resolvedBaseUrl =
    metadataBaseUrl !== null
      ? resolveIndexUrl(configuredBaseUrl, metadataBaseUrl)
      : metadataManifestUrl !== null && /^https?:\/\//iu.test(metadataManifestUrl)
        ? directoryUrl(metadataManifestUrl)
        : configuredBaseUrl;

  return resolvedBaseUrl === "/" ? "/" : resolvedBaseUrl.replace(/\/+$/u, "");
}

function directoryUrl(value: string): string {
  const url = new URL(value);
  url.pathname = url.pathname.replace(/\/[^/]*$/u, "");
  url.search = "";
  url.hash = "";
  return url.toString().replace(/\/$/u, "");
}

export function validateStreetKitSiteId(siteId: string): boolean {
  return STREETKIT_SITE_ID_PATTERN.test(siteId);
}

function resolveAccessConfig(config: {
  baseUrl: string;
  mode?: StreetKitAccessMode | undefined;
  publicMode?: boolean | undefined;
  siteId?: string | undefined;
}): StreetKitResolvedAccess {
  const pathSiteId = siteIdFromIndexBaseUrl(config.baseUrl);
  const siteId = normaliseConfiguredSiteId(config.siteId);
  const modeFromPublicFlag =
    config.publicMode === undefined
      ? undefined
      : config.publicMode
        ? "public"
        : "registered";
  const configuredMode = config.mode ?? modeFromPublicFlag;

  if (
    config.mode !== undefined &&
    modeFromPublicFlag !== undefined &&
    config.mode !== modeFromPublicFlag
  ) {
    throw new StreetKitConfigError("StreetKit mode conflicts with publicMode.");
  }

  if (
    configuredMode !== undefined &&
    configuredMode !== "public" &&
    configuredMode !== "registered"
  ) {
    throw new StreetKitConfigError("StreetKit mode must be public or registered.");
  }

  const mode: StreetKitAccessMode =
    configuredMode ?? (siteId !== null || pathSiteId !== null ? "registered" : "public");

  if (pathSiteId !== null && !validateStreetKitSiteId(pathSiteId)) {
    throw new StreetKitConfigError("StreetKit indexBaseUrl contains an invalid siteId.", {
      indexBaseUrl: config.baseUrl,
      siteId: pathSiteId
    });
  }

  if (siteId !== null && !validateStreetKitSiteId(siteId)) {
    throw new StreetKitConfigError("StreetKit siteId must match sk_live_* or sk_test_*.", {
      siteId
    });
  }

  if (siteId !== null && pathSiteId !== null && siteId !== pathSiteId) {
    throw new StreetKitConfigError("StreetKit siteId does not match indexBaseUrl.", {
      indexBaseUrl: config.baseUrl,
      siteId
    });
  }

  if (mode === "public") {
    if (siteId !== null) {
      throw new StreetKitConfigError("StreetKit siteId cannot be used with public mode.", {
        siteId
      });
    }

    if (pathSiteId !== null) {
      throw new StreetKitConfigError("StreetKit registered indexBaseUrl cannot be used with public mode.", {
        indexBaseUrl: config.baseUrl,
        siteId: pathSiteId
      });
    }

    return {
      mode: "public",
      siteId: null,
      appendSiteParam: false,
      siteQueryParam: "site"
    };
  }

  const effectiveSiteId = siteId ?? pathSiteId;

  if (effectiveSiteId === null && configuredMode !== "registered") {
    throw new StreetKitConfigError("StreetKit registered mode requires a siteId.");
  }

  return {
    mode: "registered",
    siteId: effectiveSiteId,
    appendSiteParam: true,
    siteQueryParam: "site"
  };
}

function resolveMetadataAccess(
  configuredAccess: StreetKitResolvedAccess,
  meta: StreetKitMetaJson,
  baseUrl: string
): StreetKitResolvedAccess {
  const metadataAccess = meta.access;
  const metadataStatus = metadataAccess?.status?.trim().toLowerCase();

  if (metadataStatus === "disabled" || metadataStatus === "rotated") {
    throw new StreetKitConfigError("StreetKit index metadata is not active.", {
      indexBaseUrl: baseUrl,
      ...(configuredAccess.siteId === null ? {} : { siteId: configuredAccess.siteId })
    });
  }

  const metadataMode = accessModeFromString(metadataAccess?.mode);
  const metadataSiteId = stringFrom(metadataAccess?.siteId, metadataAccess?.site_id);
  const metadataSiteRequired = siteRequiredFrom(metadataAccess);

  if (
    configuredAccess.mode === "public" &&
    configuredAccess.siteId === null &&
    (metadataMode === "registered" || metadataSiteRequired === true)
  ) {
    throw new StreetKitConfigError("StreetKit registered metadata requires config.siteId.", {
      indexBaseUrl: baseUrl,
      reason: "missing_site_id"
    });
  }

  if (metadataMode !== null && metadataMode !== configuredAccess.mode) {
    throw new StreetKitConfigError("StreetKit access mode does not match index metadata.", {
      indexBaseUrl: baseUrl,
      ...(configuredAccess.siteId === null ? {} : { siteId: configuredAccess.siteId })
    });
  }

  if (configuredAccess.mode === "public") {
    if (metadataSiteRequired === true) {
      throw new StreetKitConfigError("StreetKit public mode cannot use site-required metadata.", {
        indexBaseUrl: baseUrl,
        reason: "missing_site_id"
      });
    }

    return configuredAccess;
  }

  if (metadataSiteId !== null) {
    if (!validateStreetKitSiteId(metadataSiteId)) {
      throw new StreetKitConfigError("StreetKit metadata contains an invalid siteId.", {
        indexBaseUrl: baseUrl,
        siteId: metadataSiteId
      });
    }

    if (configuredAccess.siteId !== null && metadataSiteId !== configuredAccess.siteId) {
      throw new StreetKitConfigError("StreetKit configured siteId does not match index metadata.", {
        indexBaseUrl: baseUrl,
        ...(configuredAccess.siteId === null ? {} : { siteId: configuredAccess.siteId })
      });
    }
  }

  const effectiveSiteId = configuredAccess.siteId ?? metadataSiteId;

  if (metadataSiteRequired === false) {
    throw new StreetKitConfigError("StreetKit registered metadata must require a siteId.", {
      indexBaseUrl: baseUrl,
      ...(effectiveSiteId === null ? {} : { siteId: effectiveSiteId })
    });
  }

  if (effectiveSiteId === null) {
    throw new StreetKitConfigError("StreetKit registered metadata requires a siteId.", {
      indexBaseUrl: baseUrl
    });
  }

  const queryParamDisabled =
    metadataAccess?.queryParam === null || metadataAccess?.query_param === null;
  const metadataQueryParam = queryParamDisabled
    ? null
    : stringFrom(metadataAccess?.queryParam, metadataAccess?.query_param) ??
      configuredAccess.siteQueryParam;

  if (metadataQueryParam !== null && !STREETKIT_SITE_QUERY_PARAM_PATTERN.test(metadataQueryParam)) {
    throw new StreetKitConfigError("StreetKit metadata contains an invalid site query parameter.", {
      indexBaseUrl: baseUrl,
      ...(effectiveSiteId === null ? {} : { siteId: effectiveSiteId })
    });
  }

  return {
    mode: "registered",
    siteId: effectiveSiteId,
    appendSiteParam: queryParamDisabled === false,
    siteQueryParam: metadataQueryParam ?? configuredAccess.siteQueryParam
  };
}

export function appendStreetKitSiteQuery(
  url: string,
  siteId: string,
  siteQueryParam = "site"
): string {
  const absoluteUrl = /^https?:\/\//iu.test(url);
  const startsWithSlash = url.startsWith("/");
  const parseUrl = absoluteUrl || startsWithSlash ? url : `/${url}`;
  const parsed = new URL(parseUrl, "https://streetkit.invalid");

  parsed.searchParams.set(siteQueryParam, siteId);

  if (absoluteUrl) {
    return parsed.toString();
  }

  const relativeUrl = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  return startsWithSlash ? relativeUrl : relativeUrl.replace(/^\//u, "");
}

function withSiteQuery(url: string, access: StreetKitResolvedAccess): string {
  if (
    access.mode !== "registered" ||
    access.appendSiteParam === false ||
    access.siteId === null
  ) {
    return url;
  }

  return appendStreetKitSiteQuery(url, access.siteId, access.siteQueryParam);
}

function normaliseConfiguredSiteId(siteId: string | undefined): string | null {
  if (siteId === undefined) {
    return null;
  }

  const trimmed = siteId.trim();

  if (trimmed.length === 0) {
    throw new StreetKitConfigError("StreetKit siteId must not be empty.");
  }

  return trimmed;
}

function siteIdFromIndexBaseUrl(baseUrl: string): string | null {
  const pathname = indexBasePath(baseUrl);
  const match = /(?:^|\/)c\/([^/]+)\/v1(?:\/)?$/u.exec(pathname);
  return match?.[1] ?? null;
}

function indexBasePath(baseUrl: string): string {
  if (/^https?:\/\//iu.test(baseUrl)) {
    try {
      return new URL(baseUrl).pathname.replace(/\/+$/u, "");
    } catch {
      throw new StreetKitConfigError("StreetKit indexBaseUrl must be a valid URL or path.", {
        indexBaseUrl: baseUrl
      });
    }
  }

  return baseUrl.replace(/[?#].*$/u, "").replace(/\/+$/u, "");
}

function accessModeFromString(value: string | undefined): StreetKitAccessMode | null {
  if (value === "public" || value === "registered") {
    return value;
  }

  return null;
}

function siteRequiredFrom(access: StreetKitAccessMetadata | undefined): boolean | null {
  const values = [
    access?.siteRequired,
    access?.site_required,
    access?.siteIdRequired,
    access?.site_id_required
  ].filter((value): value is boolean => typeof value === "boolean");

  if (values.includes(true)) {
    return true;
  }

  if (values.includes(false)) {
    return false;
  }

  return null;
}

export function shardKeysForQuery(
  query: string,
  options: ShardKeyOptions = {}
): string[] {
  const minQueryLength = options.minQueryLength ?? DEFAULT_STREETKIT_MIN_QUERY_LENGTH;
  const prefixLength = options.prefixLength ?? PREFIX_SHARD_LENGTH;
  const maxShardKeys = options.maxShardKeys ?? MAX_QUERY_SHARD_KEYS;

  if (normaliseQueryForLength(query).length < minQueryLength) {
    return [];
  }

  const keys = queryShardTerms(query).map((term) => shardKeyForTerm(term, prefixLength));

  return uniqueTokens(keys.filter((key): key is string => key !== null)).slice(0, maxShardKeys);
}

export function shardPathForKey(key: string): string {
  return `shards/prefix/${key.charAt(0)}/${key}.json`;
}

export function normaliseQueryForLength(query: string): string {
  return normaliseMacrons(query.toLowerCase())
    .replace(/[^\p{L}\p{N}]+/gu, "")
    .trim();
}

export function normaliseQueryCacheKey(query: string): string {
  return normaliseMacrons(query.toLowerCase())
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .replace(/\s+/gu, " ");
}

export function toSelectedAddress(
  detail: AddressDetail,
  _record: CompactShardAddress | null = null,
  score?: number
): StreetKitSelectedAddress {
  return {
    id: detail.id,
    label: detail.label,
    components: {
      unit: detail.components.unit,
      level: detail.components.level,
      number: detail.components.number,
      numberSuffix: detail.components.numberSuffix,
      numberHigh: detail.components.numberHigh,
      street: detail.components.street,
      streetName: detail.components.streetName,
      streetType: detail.components.streetType,
      streetSuffix: detail.components.streetSuffix,
      suburb: detail.components.suburb,
      city: detail.components.city,
      postcode: detail.components.postcode,
      territorialAuthority: detail.components.territorialAuthority,
      region: detail.components.region,
      country: detail.components.country
    },
    location: selectedLocation(detail.location),
    source: {
      provider: "LINZ",
      dataset: "NZ Addresses",
      layer: "123113",
      version: detail.source.version,
      exportedAt: detail.source.exportedAt ?? null,
      builtAt: detail.source.builtAt ?? null,
      licence: detail.source.licence ?? STREETKIT_LINZ_LICENCE
    },
    confidence: {
      score: Number.isFinite(score) ? score ?? null : null,
      matchType: "autocomplete"
    }
  };
}

function detailPathForId(
  id: string,
  options: {
    template?: string | undefined;
    bucketStrategy?: string | undefined;
    bucketPrefixLength?: number | undefined;
  } = {}
): string {
  const template = options.template ?? "detail/{bucket}.json";
  const bucket = usesLegacyPerAddressTemplate(template)
    ? legacyPerAddressBucketForId(id)
    : detailBucketForId(id, detailBucketPrefix(options));
  const safeId = safeDetailFileStem(id);
  const addressId = /^linz:(.+)$/u.exec(id)?.[1] ?? id;

  return template
    .replaceAll("{bucket}", bucket)
    .replaceAll("{safeId}", safeId)
    .replaceAll("{safe_id}", safeId)
    .replaceAll("{streetkit_id}", id)
    .replaceAll("{id}", id)
    .replaceAll("{addressId}", addressId)
    .replaceAll("{address_id}", addressId);
}

function usesLegacyPerAddressTemplate(template: string): boolean {
  return /\{(?:safeId|safe_id|addressId|address_id)\}/u.test(template);
}

function legacyPerAddressBucketForId(id: string): string {
  const linzAddressId = /^linz:(\d+)/u.exec(id)?.[1];

  if (linzAddressId !== undefined) {
    return linzAddressId.slice(0, 2);
  }

  return detailBucketForId(id).slice(0, 2);
}

function detailBucketForId(id: string, prefixLength?: number | undefined): string {
  return sharedDetailBucketForId(id, prefixLength);
}

function detailBucketPrefix(options: {
  bucketStrategy?: string | undefined;
  bucketPrefixLength?: number | undefined;
}): number {
  if (options.bucketPrefixLength !== undefined) {
    return options.bucketPrefixLength;
  }

  return detailBucketPrefixLengthFromStrategy(options.bucketStrategy);
}

function detailFromPayload(payload: StreetKitDetailPayload, id: string): AddressDetail | null {
  if (isAddressDetail(payload)) {
    return payload.id === id ? payload : null;
  }

  const detail = payload.records?.[id];
  return detail !== undefined && isAddressDetail(detail) ? detail : null;
}

function isAddressDetail(value: unknown): value is AddressDetail {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { id?: unknown }).id === "string" &&
    typeof (value as { label?: unknown }).label === "string"
  );
}

function safeDetailFileStem(id: string): string {
  return id
    .toLowerCase()
    .replace(/:/gu, "_")
    .replace(/[^a-z0-9_-]+/gu, "_")
    .replace(/^_+|_+$/gu, "");
}

function shardKeyForTerm(term: string, prefixLength: number): string | null {
  const token = tokeniseRawQuery(term)[0];

  if (token === undefined || token.length < prefixLength) {
    return null;
  }

  return token.slice(0, prefixLength);
}

function tokeniseRawQuery(query: string): string[] {
  const folded = normaliseMacrons(query.toLowerCase());
  return folded
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .split(/\s+/u)
    .filter(Boolean);
}

function queryShardTerms(query: string): string[] {
  const addressTokens = tokeniseAddressQuery(query);
  const rawTokens = tokeniseRawQuery(query);
  const addressTerms = significantQueryTerms(addressTokens);
  const rawTerms = significantQueryTerms(rawTokens);

  return uniqueTokens([
    ...addressTerms,
    ...adjacentCompositeTerms(addressTokens),
    ...roadPhraseCompositeTerms(addressTokens),
    ...rawTerms,
    ...adjacentCompositeTerms(rawTokens),
    ...roadPhraseCompositeTerms(rawTokens)
  ]);
}

function adjacentCompositeTerms(tokens: string[]): string[] {
  const composites: string[] = [];

  for (let index = 0; index < tokens.length - 1; index += 1) {
    const left = tokens[index];
    const right = tokens[index + 1];

    if (left === undefined || right === undefined) {
      continue;
    }

    if (
      !isShardCandidateTerm(left) ||
      !isShardCandidateTerm(right) ||
      isGenericRoadTypeTerm(left)
    ) {
      continue;
    }

    const composite = `${left}${right}`;

    if (!/^[a-z]/u.test(composite)) {
      continue;
    }

    composites.push(composite);
  }

  return composites.filter(isShardCandidateTerm);
}

function roadPhraseCompositeTerms(tokens: string[]): string[] {
  const composites: string[] = [];

  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];

    if (token === undefined || !isGenericRoadTypeTerm(token)) {
      continue;
    }

    const nameTokens = tokens
      .slice(0, index)
      .filter(
        (candidate) =>
          !isGenericRoadTypeTerm(candidate) &&
          isShardCandidateTerm(candidate) &&
          /^[a-z]/u.test(candidate)
      );
    const lastNameToken = nameTokens.at(-1);
    const bases: string[] = [];

    if (lastNameToken !== undefined) {
      bases.push(`${lastNameToken}${token}`);
    }

    if (nameTokens.length > 1) {
      bases.push(`${nameTokens.join("")}${token}`);
    }

    const roadNumber = numberedStateHighwayToken(tokens, index, nameTokens);

    composites.push(...bases);

    if (roadNumber !== null) {
      composites.push(...bases.map((base) => `${base}${roadNumber}`));
    }
  }

  return composites.filter((candidate) => /^[a-z]/u.test(candidate) && isShardCandidateTerm(candidate));
}

function numberedStateHighwayToken(
  tokens: string[],
  roadTypeIndex: number,
  nameTokens: string[]
): string | null {
  const roadType = tokens[roadTypeIndex];
  const next = tokens[roadTypeIndex + 1];

  if (roadType !== "highway" || !nameTokens.includes("state")) {
    return null;
  }

  return next !== undefined && /^\d+[a-z]?$/u.test(next) ? next : null;
}

function significantQueryTerms(tokens: string[]): string[] {
  const candidates = tokens.filter(isShardCandidateTerm);
  const selectiveTerms = candidates.filter((token) => !isGenericRoadTypeTerm(token));
  return selectiveTerms.length > 0 ? selectiveTerms : candidates;
}

function isShardCandidateTerm(token: string): boolean {
  if (isUnitMarker(token)) {
    return false;
  }

  if (/^\d{4}$/u.test(token)) {
    return true;
  }

  if (/^\d+[a-z]?$/u.test(token)) {
    return false;
  }

  return /^[a-z0-9]{2,}$/u.test(token);
}

function isGenericRoadTypeTerm(token: string): boolean {
  return genericRoadTypeTerms.has(token);
}

function hasLeadingAddressNumberIntent(rawQuery: string): boolean {
  const parsed = parseAddressQuery(rawQuery);
  const addressNumber = parsed.addressNumber?.token;

  if (addressNumber === undefined || isPostcodeToken(addressNumber)) {
    return false;
  }

  const addressNumberIndex = parsed.tokens.findIndex((token) => token === addressNumber);
  const firstRoadTypeIndex = parsed.tokens.findIndex(isGenericRoadTypeTerm);

  return (
    addressNumberIndex !== -1 &&
    isAddressNumberToken(addressNumber) &&
    (firstRoadTypeIndex === -1 || addressNumberIndex < firstRoadTypeIndex)
  );
}

function selectedLocation(location: AddressDetail["location"]): StreetKitSelectedAddress["location"] {
  if (
    location.lat === null ||
    location.lon === null ||
    !isLikelyNzCoordinate(location.lon, location.lat)
  ) {
    return {
      lat: null,
      lon: null
    };
  }

  return {
    lat: location.lat,
    lon: location.lon
  };
}

function isLikelyNzCoordinate(longitude: number, latitude: number): boolean {
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
    return false;
  }

  const inMainLongitudeRange = longitude >= 165 && longitude <= 180;
  const inAntimeridianLongitudeRange = longitude >= -180 && longitude <= -175;

  return (inMainLongitudeRange || inAntimeridianLongitudeRange) && latitude >= -53 && latitude <= -29;
}

type SearchCacheKeyParts = {
  datasetVersion: string;
  limit: number;
  minQueryLength: number;
  prefixLength: number;
  query: string;
  shardKeys: string[];
};

type QueryRefinementCacheEntry = {
  datasetVersion: string;
  minQueryLength: number;
  prefixLength: number;
  query: string;
  records: CompactShardAddress[];
  shardKeys: string[];
  broadTerms: string[];
  routeTerms: string[];
};

type ShardSelectionStatus = "search" | "too_broad" | "no_route";

type ShardSelection = {
  status: ShardSelectionStatus;
  keys: string[];
  broadTerms: string[];
  routeTerms: string[];
};

type AdaptiveRoutePlan = {
  key: string;
  route: StreetKitShardRoute;
  termLength: number;
  gzipBytes: number | null;
  recordCount: number | null;
};

function selectShardKeysForSearch(
  rawQuery: string,
  info: StreetKitIndexInfo,
  minQueryLength: number
): ShardSelection {
  if (
    info.shardStrategy === ADAPTIVE_PREFIX_SHARD_STRATEGY_NAME &&
    info.routing?.routes !== undefined
  ) {
    return selectAdaptiveShardKeysForFetch(rawQuery, info.routing, minQueryLength);
  }

  const keys = selectShardKeysForFetch(
    shardKeysForQuery(rawQuery, {
      minQueryLength,
      prefixLength: info.prefixLength
    }),
    info
  );

  return {
    status: keys.length === 0 ? "no_route" : "search",
    keys,
    broadTerms: [],
    routeTerms: keys
  };
}

function selectAdaptiveShardKeysForFetch(
  rawQuery: string,
  routing: StreetKitShardRoutingJson,
  minQueryLength: number
): ShardSelection {
  if (normaliseQueryForLength(rawQuery).length < minQueryLength) {
    return {
      status: "no_route",
      keys: [],
      broadTerms: [],
      routeTerms: []
    };
  }

  const routes = routing.routes ?? {};
  const basePrefixLength =
    numberFrom(routing.defaults?.base_prefix_length, routing.defaults?.basePrefixLength) ??
    PREFIX_SHARD_LENGTH;
  const routePlans = new Map<string, AdaptiveRoutePlan>();
  const broadTerms = new Set<string>();
  const routeTerms = new Set<string>();

  for (const term of queryShardTerms(rawQuery)) {
    const token = tokeniseRawQuery(term)[0];

    if (
      token === undefined ||
      token.length < basePrefixLength ||
      (token.length < minQueryLength && !/^\d{4}$/u.test(token))
    ) {
      continue;
    }

    const plan = adaptiveRoutePlanForToken(token, routes, basePrefixLength);

    if (plan !== null && plan.route.path !== undefined && isTooBroadRoute(plan.route) === false) {
      const existingPlan = routePlans.get(plan.key);

      if (existingPlan === undefined || compareAdaptiveRoutePlans(plan, existingPlan) < 0) {
        routePlans.set(plan.key, plan);
      }
      routeTerms.add(token);
      continue;
    }

    if (plan !== null && isTooBroadRoute(plan.route)) {
      broadTerms.add(token);
    }
  }

  const keys = [...routePlans.values()]
    .sort(compareAdaptiveRoutePlans)
    .slice(0, 1)
    .map((plan) => plan.key);

  if (keys.length > 0) {
    return {
      status: "search",
      keys,
      broadTerms: [...broadTerms],
      routeTerms: [...routeTerms]
    };
  }

  return {
    status: broadTerms.size > 0 ? "too_broad" : "no_route",
    keys: [],
    broadTerms: [...broadTerms],
    routeTerms: []
  };
}

function emptySearchPlan(
  status: "empty" | "too_short",
  minQueryLength: number
): StreetKitSearchPlan {
  return {
    status,
    message:
      status === "empty"
        ? "Type to search for an address."
        : `Type at least ${minQueryLength} characters to search.`,
    minQueryLength,
    shardKeys: [],
    broadTerms: [],
    routeTerms: []
  };
}

function selectionToSearchPlan(
  selection: ShardSelection,
  minQueryLength: number
): StreetKitSearchPlan {
  return {
    status: selection.status,
    message: searchPlanMessage(selection.status, minQueryLength),
    minQueryLength,
    shardKeys: selection.keys,
    broadTerms: selection.broadTerms,
    routeTerms: selection.routeTerms
  };
}

function refinementSearchPlan(
  entry: QueryRefinementCacheEntry,
  minQueryLength: number
): StreetKitSearchPlan {
  return {
    status: "search",
    message: "Search route selected from cached candidates.",
    minQueryLength,
    shardKeys: entry.shardKeys,
    broadTerms: entry.broadTerms,
    routeTerms: entry.routeTerms
  };
}

function searchPlanMessage(status: ShardSelectionStatus, minQueryLength: number): string {
  if (status === "search") {
    return "Search route selected.";
  }

  if (status === "too_broad") {
    return "That query is too broad for address suggestions. Type a street name, address number, or suburb.";
  }

  return `No searchable route found. Type at least ${minQueryLength} characters, or add a street name or address number.`;
}

function adaptiveRoutePlanForToken(
  token: string,
  routes: Record<string, StreetKitShardRoute>,
  basePrefixLength: number
): AdaptiveRoutePlan | null {
  for (let length = token.length; length >= basePrefixLength; length -= 1) {
    const key = token.slice(0, length);
    const route = routes[key];

    if (route === undefined) {
      continue;
    }

    return {
      key,
      route,
      termLength: token.length,
      gzipBytes: numberFrom(route.gzip_bytes, route.gzipBytes),
      recordCount: numberFrom(route.record_count, route.recordCount)
    };
  }

  return null;
}

function broadChildFallbackKeys(
  broadTerms: string[],
  routing: StreetKitShardRoutingJson
): string[] {
  const routes = routing.routes ?? {};
  const basePrefixLength =
    numberFrom(routing.defaults?.base_prefix_length, routing.defaults?.basePrefixLength) ??
    PREFIX_SHARD_LENGTH;
  const childRoutes = new Map<string, StreetKitShardRoute>();

  for (const term of broadTerms) {
    const token = tokeniseRawQuery(term)[0];

    if (token === undefined) {
      continue;
    }

    const plan = adaptiveRoutePlanForToken(token, routes, basePrefixLength);

    if (plan === null || !isTooBroadRoute(plan.route) || plan.route.children === undefined) {
      continue;
    }

    for (const child of plan.route.children) {
      const route = routes[child];

      if (
        route === undefined ||
        route.path === undefined ||
        route.path.trim().length === 0 ||
        isTooBroadRoute(route)
      ) {
        continue;
      }

      childRoutes.set(child, route);
    }
  }

  if (childRoutes.size === 0 || childRoutes.size > MAX_BROAD_CHILD_FALLBACK_KEYS) {
    return [];
  }

  const routeList = [...childRoutes.values()];
  const totalRecords = sumKnownRouteMetric(routeList, (route) =>
    numberFrom(route.record_count, route.recordCount)
  );
  const totalGzipBytes = sumKnownRouteMetric(routeList, (route) =>
    numberFrom(route.gzip_bytes, route.gzipBytes)
  );

  if (
    totalRecords === null ||
    totalGzipBytes === null ||
    totalRecords > MAX_BROAD_CHILD_FALLBACK_RECORDS ||
    totalGzipBytes > MAX_BROAD_CHILD_FALLBACK_GZIP_BYTES
  ) {
    return [];
  }

  return [...childRoutes.keys()];
}

function sumKnownRouteMetric(
  routes: StreetKitShardRoute[],
  read: (route: StreetKitShardRoute) => number | null
): number | null {
  let total = 0;

  for (const route of routes) {
    const value = read(route);

    if (value === null) {
      return null;
    }

    total += value;
  }

  return total;
}

function compareAdaptiveRoutePlans(left: AdaptiveRoutePlan, right: AdaptiveRoutePlan): number {
  const specificityOrder = right.termLength - left.termLength;

  if (specificityOrder !== 0) {
    return specificityOrder;
  }

  const gzipOrder =
    (left.gzipBytes ?? Number.POSITIVE_INFINITY) -
    (right.gzipBytes ?? Number.POSITIVE_INFINITY);

  if (gzipOrder !== 0) {
    return gzipOrder;
  }

  const recordOrder =
    (left.recordCount ?? Number.POSITIVE_INFINITY) -
    (right.recordCount ?? Number.POSITIVE_INFINITY);

  if (recordOrder !== 0) {
    return recordOrder;
  }

  return right.key.length - left.key.length;
}

function isTooBroadRoute(route: StreetKitShardRoute): boolean {
  return route.too_broad === true || route.tooBroad === true;
}

function selectShardKeysForFetch(keys: string[], info: StreetKitIndexInfo): string[] {
  const manifestFilteredKeys = filterManifestShardKeys(keys, info.manifest);
  return preferNonLargeShardKeys(manifestFilteredKeys, info.largeShardKeys);
}

function filterManifestShardKeys(
  keys: string[],
  manifest: StreetKitManifestJson | null
): string[] {
  const files = manifest?.shards?.files;

  if (files === undefined || files.length === 0) {
    return keys;
  }

  const manifestKeys = new Set(
    files
      .map((file) => file.key)
      .filter((key): key is string => typeof key === "string" && key.trim().length > 0)
  );

  if (manifestKeys.size === 0) {
    return keys;
  }

  return keys.filter((key) => manifestKeys.has(key));
}

function preferNonLargeShardKeys(keys: string[], largeShardKeys: string[]): string[] {
  if (keys.length <= 1 || largeShardKeys.length === 0) {
    return keys;
  }

  const largeKeys = new Set(largeShardKeys);
  const nonLargeKeys = keys.filter((key) => !largeKeys.has(key));

  return nonLargeKeys.length > 0 ? nonLargeKeys : keys.slice(0, 1);
}

function manifestWarnings(manifest: StreetKitManifestJson | null): string[] {
  if (!Array.isArray(manifest?.warnings)) {
    return [];
  }

  return manifest.warnings.filter(
    (warning): warning is string => typeof warning === "string" && warning.trim().length > 0
  );
}

function largeShardKeysFromManifest(manifest: StreetKitManifestJson | null): string[] {
  const files = manifest?.shards?.files;

  if (files === undefined || files.length === 0) {
    return [];
  }

  const hardGzipBytes =
    numberFrom(manifest?.shards?.hard_gzip_bytes, manifest?.shards?.hardGzipBytes) ??
    DEFAULT_STREETKIT_HARD_SHARD_GZIP_BYTES;
  const warningText = manifestWarnings(manifest).join("\n").toLowerCase();
  const largestPath = stringFrom(
    manifest?.largest_shard_size?.path,
    manifest?.largestShardSize?.path
  );
  const largestGzipBytes = numberFrom(
    manifest?.largest_shard_size?.gzip_bytes,
    manifest?.largest_shard_size?.gzipBytes,
    manifest?.largestShardSize?.gzip_bytes,
    manifest?.largestShardSize?.gzipBytes
  );
  const largeKeys = new Set<string>();

  for (const file of files) {
    if (typeof file.key !== "string" || file.key.trim().length === 0) {
      continue;
    }

    const gzipBytes = numberFrom(file.gzip_bytes, file.gzipBytes);

    if (gzipBytes !== null && gzipBytes > hardGzipBytes) {
      largeKeys.add(file.key);
      continue;
    }

    if (
      largestPath !== null &&
      file.path === largestPath &&
      largestGzipBytes !== null &&
      largestGzipBytes > hardGzipBytes
    ) {
      largeKeys.add(file.key);
      continue;
    }

    if (warningText.length > 0 && warningText.includes(file.path.toLowerCase())) {
      largeKeys.add(file.key);
    }
  }

  return [...largeKeys];
}

function searchCacheKey(parts: SearchCacheKeyParts): string {
  return JSON.stringify([
    parts.datasetVersion,
    parts.prefixLength,
    parts.minQueryLength,
    parts.limit,
    parts.query,
    parts.shardKeys
  ]);
}

function queryRefinementCacheKey(parts: {
  datasetVersion: string;
  minQueryLength: number;
  prefixLength: number;
  query: string;
}): string {
  return JSON.stringify([
    parts.datasetVersion,
    parts.prefixLength,
    parts.minQueryLength,
    parts.query
  ]);
}

function isReusableCachedQuery(previousQuery: string, currentQuery: string): boolean {
  if (previousQuery.length === 0 || currentQuery.length === 0 || previousQuery === currentQuery) {
    return false;
  }

  if (currentQuery.startsWith(`${previousQuery} `)) {
    return true;
  }

  if (previousQuery.startsWith(`${currentQuery} `)) {
    return true;
  }

  const previousTokens = previousQuery.split(" ");
  const currentTokens = currentQuery.split(" ");

  if (previousTokens.length !== currentTokens.length) {
    return false;
  }

  for (let index = 0; index < previousTokens.length - 1; index += 1) {
    if (previousTokens[index] !== currentTokens[index]) {
      return false;
    }
  }

  const previousLastToken = previousTokens.at(-1);
  const currentLastToken = currentTokens.at(-1);

  return (
    previousLastToken !== undefined &&
    currentLastToken !== undefined &&
    (currentLastToken.startsWith(previousLastToken) ||
      previousLastToken.startsWith(currentLastToken))
  );
}

function normaliseCacheSize(value: number | undefined, fallback: number): number {
  if (value === undefined) {
    return fallback;
  }

  if (!Number.isFinite(value) || value < 0) {
    return fallback;
  }

  return Math.floor(value);
}

function readCachedValue<K, V>(cache: Map<K, V>, key: K): V | undefined {
  const value = cache.get(key);

  if (value === undefined) {
    return undefined;
  }

  cache.delete(key);
  cache.set(key, value);
  return value;
}

function setBoundedCacheValue<K, V>(cache: Map<K, V>, key: K, value: V, maxSize: number): void {
  if (maxSize <= 0) {
    return;
  }

  if (cache.has(key)) {
    cache.delete(key);
  }

  cache.set(key, value);

  while (cache.size > maxSize) {
    const oldest = cache.keys().next();

    if (oldest.done === true) {
      break;
    }

    cache.delete(oldest.value);
  }
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted !== true) {
    return;
  }

  if (typeof DOMException === "function") {
    throw new DOMException("StreetKit search aborted.", "AbortError");
  }

  const error = new Error("StreetKit search aborted.");
  error.name = "AbortError";
  throw error;
}

function numberFrom(...values: Array<number | null | undefined>): number | null {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }

  return null;
}

function stringFrom(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }

  return null;
}

function booleanFrom(...values: Array<boolean | null | undefined>): boolean | null {
  for (const value of values) {
    if (typeof value === "boolean") {
      return value;
    }
  }

  return null;
}

function uniqueTokens(values: string[]): string[] {
  return [...new Set(values.filter((value) => value.length > 0))];
}

const genericRoadTypeTerms = new Set([
  "avenue",
  "ave",
  "av",
  "court",
  "ct",
  "crescent",
  "cres",
  "drive",
  "dr",
  "highway",
  "hwy",
  "lane",
  "ln",
  "place",
  "pl",
  "road",
  "rd",
  "street",
  "st",
  "terrace",
  "ter"
]);

function isUnitMarker(token: string): boolean {
  return (
    token === "unit" ||
    token === "flat" ||
    token === "apartment" ||
    token === "apt" ||
    token === "suite" ||
    token === "room" ||
    token === "rm" ||
    token === "villa"
  );
}
